import React, { useMemo } from 'react'
import { Modal, Form, Button, Spinner, Row, Col } from 'react-bootstrap'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useIntl } from 'react-intl'
import {
  MenuItem,
  MenuLocation,
  MENU_LOCATIONS,
  mergeTranslations,
  displayLabel,
  collectDescendantIds,
} from './menuNavigationUtils'

export interface MenuItemEditModalProps {
  show: boolean
  onHide: () => void
  item: MenuItem | null
  selectedLanguage: string
  allItems: MenuItem[]
  defaultLocation: MenuLocation
  pageUrlSuggestions: string[]
  onSubmit: (values: {
    label: string
    url: string
    parent: string | null
    location: MenuLocation
    openInNewTab: boolean
    icon: string
    isActive: boolean
    translationsPayload: { language: string; label: string }[]
  }) => Promise<void>
  saving: boolean
}

const MenuItemEditModal: React.FC<MenuItemEditModalProps> = ({
  show,
  onHide,
  item,
  selectedLanguage,
  allItems,
  defaultLocation,
  pageUrlSuggestions,
  onSubmit,
  saving,
}) => {
  const intl = useIntl()

  const parentOptions = useMemo(() => {
    const loc = item?.location ?? defaultLocation
    const exclude = item ? collectDescendantIds(item._id, allItems) : new Set<string>()
    if (item) exclude.add(item._id)

    return allItems.filter(
      (m) =>
        m.location === loc &&
        !exclude.has(m._id) &&
        (item ? m._id !== item._id : true),
    )
  }, [allItems, item, defaultLocation])

  const validationSchema = useMemo(
    () =>
      Yup.object({
        label: Yup.string().required(
          intl.formatMessage({ id: 'MENU_NAVIGATION.VALIDATION.LABEL_REQUIRED' }),
        ),
        url: Yup.string()
          .required(intl.formatMessage({ id: 'MENU_NAVIGATION.VALIDATION.URL_REQUIRED' }))
          .trim(),
        location: Yup.string().required(),
        icon: Yup.string(),
      }),
    [intl],
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      label: item
        ? displayLabel(item, selectedLanguage)
        : '',
      url: item?.url ?? '',
      parent: item?.parent ? String(item.parent) : '',
      location: (item?.location as MenuLocation) ?? defaultLocation,
      openInNewTab: item?.openInNewTab ?? false,
      icon: item?.icon ?? '',
      isActive: item?.isActive ?? true,
    },
    validationSchema,
    onSubmit: async (values) => {
      const translationsPayload = mergeTranslations(
        item?.translations,
        selectedLanguage,
        values.label.trim(),
      )
      await onSubmit({
        label: values.label.trim(),
        url: values.url.trim(),
        parent: values.parent ? values.parent : null,
        location: values.location as MenuLocation,
        openInNewTab: values.openInNewTab,
        icon: values.icon.trim(),
        isActive: values.isActive,
        translationsPayload,
      })
    },
  })

  return (
    <Modal
      show={show}
      onHide={onHide}
      size='lg'
      centered
      backdrop='static'
      contentClassName='rounded-4 shadow-lg border-0 overflow-hidden'
      dialogClassName='mw-650px'
    >
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Header closeButton className='border-0 pt-6 px-8 pb-2'>
          <div>
            <Modal.Title className='fw-bold fs-3 text-gray-900 mb-1'>
              {item
                ? intl.formatMessage({ id: 'MENU_NAVIGATION.MODAL.TITLE_EDIT' })
                : intl.formatMessage({ id: 'MENU_NAVIGATION.MODAL.TITLE_CREATE' })}
            </Modal.Title>
            <p className='text-muted fs-7 mb-0 fw-normal'>
              {intl.formatMessage({ id: 'MENU_NAVIGATION.MODAL.SUBTITLE' })}
            </p>
          </div>
        </Modal.Header>

        <Modal.Body className='px-8 py-6'>
          <div className='mb-8'>
            <h3 className='fs-6 fw-bold text-gray-800 mb-4 pb-2 border-bottom border-gray-200'>
              {intl.formatMessage({ id: 'MENU_NAVIGATION.MODAL.SECTION_BASIC' })}
            </h3>

              <Form.Group className='mb-5'>
                <Form.Label className='fw-semibold text-gray-800 fs-6 mb-2'>
                  {intl.formatMessage({ id: 'MENU_NAVIGATION.FORM.LABEL' })}{' '}
                  <span className='badge badge-light-primary ms-1 align-middle'>
                    {selectedLanguage.toUpperCase()}
                  </span>
                </Form.Label>
                <Form.Control
                  name='label'
                  className='form-control-solid'
                  value={formik.values.label}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!(formik.touched.label && formik.errors.label)}
                />
                <Form.Control.Feedback type='invalid' className='d-block'>
                  {formik.errors.label as string}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className='mb-5'>
                <Form.Label className='fw-semibold text-gray-800 fs-6 mb-2'>
                  {intl.formatMessage({ id: 'MENU_NAVIGATION.FORM.URL' })}
                </Form.Label>
                <Form.Control
                  name='url'
                  className='form-control-solid'
                  list='menu-page-url-suggestions'
                  value={formik.values.url}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={!!(formik.touched.url && formik.errors.url)}
                  placeholder='/about'
                />
                <datalist id='menu-page-url-suggestions'>
                  {pageUrlSuggestions.map((u) => (
                    <option key={u} value={u} />
                  ))}
                </datalist>
                <Form.Control.Feedback type='invalid' className='d-block'>
                  {formik.errors.url as string}
                </Form.Control.Feedback>
              </Form.Group>

              <Row className='g-5'>
                <Col md={6}>
                  <Form.Group className='mb-0'>
                    <Form.Label className='fw-semibold text-gray-800 fs-6 mb-2'>
                      {intl.formatMessage({ id: 'MENU_NAVIGATION.FORM.PARENT' })}
                    </Form.Label>
                    <Form.Select
                      name='parent'
                      className='form-select-solid'
                      value={formik.values.parent}
                      onChange={formik.handleChange}
                    >
                      <option value=''>
                        {intl.formatMessage({ id: 'MENU_NAVIGATION.FORM.PARENT_ROOT' })}
                      </option>
                      {parentOptions.map((m) => (
                        <option key={m._id} value={m._id}>
                          {displayLabel(m, selectedLanguage)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className='mb-0'>
                    <Form.Label className='fw-semibold text-gray-800 fs-6 mb-2'>
                      {intl.formatMessage({ id: 'MENU_NAVIGATION.FORM.LOCATION' })}
                    </Form.Label>
                    <Form.Select
                      name='location'
                      className='form-select-solid'
                      value={formik.values.location}
                      onChange={formik.handleChange}
                    >
                      {MENU_LOCATIONS.map((loc) => (
                        <option key={loc} value={loc}>
                          {intl.formatMessage({ id: `MENU_NAVIGATION.LOCATION.${loc}` })}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
          </div>

          <div>
            <h3 className='fs-6 fw-bold text-gray-800 mb-4 pb-2 border-bottom border-gray-200'>
              {intl.formatMessage({ id: 'MENU_NAVIGATION.MODAL.SECTION_ADVANCED' })}
            </h3>

              <Form.Group className='mb-6'>
                <Form.Label className='fw-semibold text-gray-800 fs-6 mb-2'>
                  <i className='bi bi-palette me-2 text-gray-500' />
                  {intl.formatMessage({ id: 'MENU_NAVIGATION.FORM.ICON' })}
                </Form.Label>
                <Form.Control
                  name='icon'
                  className='form-control-solid'
                  value={formik.values.icon}
                  onChange={formik.handleChange}
                  placeholder='bi bi-house'
                />
                <div className='form-text text-gray-500 fs-8 mt-2'>
                  {intl.formatMessage({ id: 'MENU_NAVIGATION.MODAL.ICON_HINT' })}
                </div>
              </Form.Group>

              <Row className='g-4'>
                <Col md={6}>
                  <div className='form-check mb-0'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id='menu-item-open-new-tab'
                      name='openInNewTab'
                      checked={formik.values.openInNewTab}
                      onChange={formik.handleChange}
                    />
                    <label
                      className='form-check-label fw-semibold text-gray-800'
                      htmlFor='menu-item-open-new-tab'
                    >
                      {intl.formatMessage({ id: 'MENU_NAVIGATION.FORM.OPEN_NEW_TAB' })}
                    </label>
                  </div>
                </Col>
                <Col md={6}>
                  <div className='form-check form-switch mb-0'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      role='switch'
                      id='menu-item-active'
                      name='isActive'
                      checked={formik.values.isActive}
                      onChange={formik.handleChange}
                    />
                    <label
                      className='form-check-label fw-semibold text-gray-800'
                      htmlFor='menu-item-active'
                    >
                      {intl.formatMessage({ id: 'MENU_NAVIGATION.FORM.ACTIVE' })}
                    </label>
                  </div>
                </Col>
              </Row>
          </div>
        </Modal.Body>

        <Modal.Footer className='border-0 pt-0 px-8 pb-6'>
          <Button variant='light' onClick={onHide} disabled={saving}>
            {intl.formatMessage({ id: 'ACTION.CANCEL' })}
          </Button>
          <Button
            variant='primary'
            type='submit'
            className='d-flex align-items-center gap-2'
            disabled={saving}
          >
            {saving ? <Spinner animation='border' size='sm' /> : <i className='bi bi-check-lg' />}
            {intl.formatMessage({ id: 'HEADER.SAVE' })}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default MenuItemEditModal
